import { useEffect, useState, useRef, memo } from "react";
import { useLocation } from "react-router-dom";

interface PageTransitionProps {
  children: React.ReactNode;
}

const PageTransition = memo(({ children }: PageTransitionProps) => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const previousPath = useRef(location.pathname);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip animation on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Only animate if path actually changed
    if (previousPath.current !== location.pathname) {
      setIsTransitioning(true);
      setIsVisible(false);
      
      const timer = setTimeout(() => {
        setIsVisible(true);
        previousPath.current = location.pathname;
        
        // End transition after animation completes
        setTimeout(() => setIsTransitioning(false), 300);
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  return (
    <div
      className="will-change-transform"
      style={{ 
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.995)',
        filter: isVisible ? 'blur(0)' : 'blur(2px)',
        transition: isTransitioning 
          ? 'opacity 0.2s cubic-bezier(0.16, 1, 0.3, 1), transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), filter 0.2s ease-out'
          : 'none',
      }}
    >
      {children}
    </div>
  );
});

PageTransition.displayName = 'PageTransition';

export default PageTransition;
